import { create } from 'zustand'
import { Filter, FilterName, FilterValue, toQuery } from '@/lib/filters/filters'
import { isEqual } from 'lodash'
import { devtools } from 'zustand/middleware'

export type PathParams = {
  mainCategory?: string
  category?: string
  subCategory?: string
  content?: string
  query?: string
}

export interface ClearFiltersOptions {
  onlyEnabled?: boolean
}

export interface FiltersStore {
  // UI visibility state
  filtersVisible: boolean
  previousFiltersVisible: boolean
  showFilters: (options?: { shouldPersist?: boolean }) => void
  hideFilters: (options?: { shouldPersist?: boolean }) => void

  // Path and search params
  params: PathParams
  setParams: (params: PathParams) => void
  searchParams?: URLSearchParams
  setSearchParams: (params: URLSearchParams | undefined) => void

  // Filters state
  filters: Filter[]
  setFilters: (filters: Filter[]) => void
  setFilterValue: (name: FilterName, value: FilterValue) => void
  clearFilters: (options?: ClearFiltersOptions) => void
  filtersQuery: Record<string, string>
  filtersHash: string

  // Computed values
  showFilterSidebar: boolean
  mainCategorySlugResolved: string | undefined
  userIsPremium: boolean
  setUserIsPremium: (isPremium: boolean) => void

  // Local refinement
  refinementQuery?: string
  setRefinementQuery: (query: string | undefined) => void
  availableKeywords: string[]
  appliedKeywords: string[]
  usedKeywords: string[]
  setAvailableKeywords: (keywords: string[]) => void
  setAppliedKeywords: (keywords: string[]) => void
  toggleKeyword: (keyword: string) => void
  clearRefinement: () => void
}

export const useFiltersStore = create<FiltersStore>()(
  devtools((set, get) => ({
    // UI visibility state
    filtersVisible: false,
    previousFiltersVisible: false,
    showFilters: (options = {}) => {
      console.debug('Showing filters', options)
      set((state) => ({
        filtersVisible: true,
        previousFiltersVisible: options.shouldPersist ? true : state.previousFiltersVisible,
      }))
    },
    hideFilters: (options = {}) => {
      console.debug('Hide filters', options)
      set((state) => ({
        filtersVisible: false,
        previousFiltersVisible: options.shouldPersist ? false : state.previousFiltersVisible,
      }))
    },

    // Path and search params
    params: {},
    setParams: (params) => {
      return set({
        params,
        showFilterSidebar: !!params.mainCategory && !!params.category && !!params.subCategory,
      })
    },
    searchParams: {},
    setSearchParams: (searchParams) => set({ searchParams }),

    // Filters state
    filters: [],
    setFilters: (filters: Filter[]) => {
      set((state) => {
        if (isEqual(state.filters, filters)) {
          return state
        }

        const filtersQuery = toQuery(filters)
        const filtersHash = JSON.stringify(filtersQuery)

        return {
          filters,
          filtersQuery,
          filtersHash,
        }
      })
    },
    setFilterValue: (name, value) => {
      console.debug('Setting filter value', { name, value })
      const { filters, setFilters } = get()
      const updatedFilters: Filter[] = filters.map((filter) => {
        return { ...filter, value: filter.name === name ? value : filter.value } as Filter
      })
      setFilters(updatedFilters)
    },
    clearFilters: (options?: ClearFiltersOptions) => {
      console.debug('Clearing filters')
      const { filters, setFilters } = get()
      const updatedFilters: Filter[] = filters.map((filter) => {
        if (options?.onlyEnabled && !filter.enabled) {
          return filter
        }
        return { ...filter, value: undefined } as Filter
      })
      setFilters(updatedFilters)
    },
    filtersQuery: {},
    filtersHash: '{}',

    // Computed values
    showFilterSidebar: false,
    mainCategorySlugResolved: undefined,
    // get mainCategorySlugResolved() {
    //   const { params, searchParams } = get()
    //   return params.mainCategory ?? searchParams?.get('main_category') ?? undefined
    // },
    userIsPremium: false,
    setUserIsPremium: (isPremium) => set({ userIsPremium: isPremium }),

    // Local refinement
    refinementQuery: undefined,
    setRefinementQuery: (query) => {
      console.debug('Setting refinement query', query)
      set({ refinementQuery: query })
    },
    availableKeywords: [],
    appliedKeywords: [],
    usedKeywords: [],
    setAvailableKeywords: (keywords) => {
      console.debug('Setting available keywords', keywords)

      set((state) => {
        const { appliedKeywords } = state

        const filteredKeywords = appliedKeywords.filter((keyword) => {
          return keywords.includes(keyword)
        })

        return {
          availableKeywords: keywords,
          appliedKeywords: filteredKeywords,
          usedKeywords: Array.from(new Set([...filteredKeywords].sort())),
        }
      })
    },
    setAppliedKeywords: (keywords) => {
      console.debug('Setting applied keywords', keywords)
      set(() => ({
        appliedKeywords: keywords,
        usedKeywords: Array.from(new Set([...keywords].sort())),
      }))
    },
    toggleKeyword: (keyword) =>
      set((state) => {
        console.debug('Toggling keyword', keyword)
        const { appliedKeywords } = state
        const newAppliedKeywords = appliedKeywords.includes(keyword)
          ? appliedKeywords.filter((k) => k !== keyword)
          : [...appliedKeywords, keyword]

        return {
          appliedKeywords: newAppliedKeywords,
          usedKeywords: Array.from(new Set([...newAppliedKeywords].sort())),
        }
      }),
    clearRefinement: () =>
      set({
        refinementQuery: undefined,
        appliedKeywords: [],
        usedKeywords: [],
      }),
  }))
)
